import { ReactElement } from 'react'
import { Navigate } from 'react-router-dom'
import { hasAnyRole, hasPermission, Permission, Role } from '../lib/utils'
import { User } from '@frontegg/redux-store'

interface Props {
  user: User | null | undefined
  children: ReactElement
  permission?: Permission | Permission[] | 'all'
  roles?: Role[]
  deniedRoles?: Role[] // block the route for users who hold any of these roles
  redirectTo?: string
}

const ProtectedRoute = ({ user, children, permission, roles, deniedRoles, redirectTo }: Props): ReactElement => {
  const redirectElement = <Navigate to={redirectTo ?? '/'} replace />

  // Block the route if the user holds any of the denied roles in the account they're in
  if (deniedRoles && deniedRoles.length > 0 && hasAnyRole(deniedRoles, user)) {
    return redirectElement
  }

  // If roles are required, the user must hold at least one; someone with no roles is blocked
  if (roles && roles.length > 0 && !hasAnyRole(roles, user)) {
    return redirectElement
  }

  // Check permissions if specified
  if (permission && permission !== 'all') {
    if (Array.isArray(permission)) {
      // User must have ALL permissions in the array
      const hasAllPermissions = permission.every((p) => hasPermission(p, user))
      if (!hasAllPermissions) {
        return redirectElement
      }
    } else {
      // Single permission check
      if (!hasPermission(permission, user)) {
        return redirectElement
      }
    }
  }

  return children
}

export default ProtectedRoute
